import React, { useRef, useEffect, useState, useCallback } from 'react';
import * as THREE from 'three';

/* ─── Mercator projection ─── */
const bounds = { minLat: 37, maxLat: 62, minLon: -3, maxLon: 42 };

function toFlat(lat, lon) {
  const x = ((lon - bounds.minLon) / (bounds.maxLon - bounds.minLon)) * 2 - 1;
  const y = ((lat - bounds.minLat) / (bounds.maxLat - bounds.minLat)) * 2 - 1;
  return { x: x * 4.5, y: y * 2.6 };
}

/* ─── data ─── */
const HOME = { name: 'Черкаси', lat: 49.44, lon: 32.06 };

const destinations = [
  { name: 'Київ', lat: 50.45, lon: 30.52, country: 'Україна', flag: '🇺🇦' },
  { name: 'Львів', lat: 49.84, lon: 24.03, country: 'Україна', flag: '🇺🇦' },
  { name: 'Одеса', lat: 46.48, lon: 30.73, country: 'Україна', flag: '🇺🇦' },
  { name: 'Харків', lat: 49.99, lon: 36.23, country: 'Україна', flag: '🇺🇦' },
  { name: 'Дніпро', lat: 48.46, lon: 35.05, country: 'Україна', flag: '🇺🇦' },
  { name: 'Варшава', lat: 52.23, lon: 21.01, country: 'Польща', flag: '🇵🇱' },
  { name: 'Бухарест', lat: 44.43, lon: 26.10, country: 'Румунія', flag: '🇷🇴' },
  { name: 'Прага', lat: 50.08, lon: 14.44, country: 'Чехія', flag: '🇨🇿' },
  { name: 'Братислава', lat: 48.15, lon: 17.11, country: 'Словаччина', flag: '🇸🇰' },
  { name: 'Будапешт', lat: 47.50, lon: 19.04, country: 'Угорщина', flag: '🇭🇺' },
  { name: 'Кишинів', lat: 47.01, lon: 28.86, country: 'Молдова', flag: '🇲🇩' },
  { name: 'Софія', lat: 42.70, lon: 23.32, country: 'Болгарія', flag: '🇧🇬' },
  { name: 'Вільнюс', lat: 54.69, lon: 25.28, country: 'Литва', flag: '🇱🇹' },
  { name: 'Рига', lat: 56.95, lon: 24.11, country: 'Латвія', flag: '🇱🇻' },
  { name: 'Таллінн', lat: 59.44, lon: 24.75, country: 'Естонія', flag: '🇪🇪' },
  { name: 'Берлін', lat: 52.52, lon: 13.40, country: 'Німеччина', flag: '🇩🇪' },
  { name: 'Відень', lat: 48.21, lon: 16.37, country: 'Австрія', flag: '🇦🇹' },
  { name: 'Рим', lat: 41.90, lon: 12.50, country: 'Італія', flag: '🇮🇹' },
  { name: 'Париж', lat: 48.86, lon: 2.35, country: 'Франція', flag: '🇫🇷' },
  { name: 'Амстердам', lat: 52.37, lon: 4.90, country: 'Нідерланди', flag: '🇳🇱' },
  { name: 'Брюссель', lat: 50.85, lon: 4.35, country: 'Бельгія', flag: '🇧🇪' },
];

/* ─── simplified country border outlines (lat/lon polylines) ─── */
const countryBorders = [
  // Ukraine (simplified)
  { name: 'Ukraine', color: 0x4dc247, opacity: 0.25, points: [
    [52.38,22.14],[51.51,23.61],[51.89,24.14],[51.92,25.78],[51.46,27.71],[52.11,29.25],[52.34,31.76],[51.63,33.93],[50.40,36.07],[49.60,38.01],[48.69,38.19],[47.64,37.54],[47.05,38.21],[46.65,36.81],[46.05,36.23],[45.93,35.38],[45.36,36.63],[45.19,35.96],[44.39,33.96],[44.60,33.53],[45.05,33.60],[45.33,32.51],[46.13,31.96],[46.57,32.25],[46.64,31.51],[46.05,30.00],[45.49,29.68],[45.33,28.74],[45.45,28.21],[46.18,28.25],[46.60,28.21],[47.01,28.86],[48.25,26.62],[48.45,24.87],[49.42,22.87],[49.09,22.71],[48.62,22.13],[49.09,22.18],[49.44,22.56],[50.10,24.00],[50.42,23.97],[51.62,23.49],[52.10,23.83],[52.38,22.14],
  ]},
  // Poland
  { name: 'Poland', color: 0x16b724, opacity: 0.12, points: [
    [54.37,18.76],[54.79,18.41],[54.45,16.20],[53.87,14.12],[52.83,14.14],[52.34,14.69],[51.06,14.97],[50.77,14.32],[50.36,16.01],[50.10,17.01],[49.44,18.83],[49.50,19.80],[49.09,22.18],[49.44,22.56],[50.10,24.00],[50.42,23.97],[51.62,23.49],[52.10,23.83],[52.38,22.14],[54.05,23.48],[54.37,18.76],
  ]},
  // Romania
  { name: 'Romania', color: 0x16b724, opacity: 0.12, points: [
    [48.22,22.68],[47.72,23.17],[47.96,24.89],[47.73,26.22],[48.25,26.62],[48.45,24.87],[48.22,22.68],
    [47.73,26.22],[47.01,28.86],[46.60,28.21],[46.18,28.25],[45.45,28.21],[45.15,28.79],[44.90,28.58],[44.35,28.66],[43.79,28.60],[43.70,25.50],[44.23,22.66],[44.43,22.47],[46.08,21.44],[47.00,22.21],[48.22,22.68],
  ]},
  // Germany (simplified)
  { name: 'Germany', color: 0x16b724, opacity: 0.10, points: [
    [54.80,9.45],[53.90,8.65],[53.40,7.10],[51.97,6.76],[51.44,6.19],[50.37,6.36],[49.46,6.73],[47.59,7.58],[47.55,9.58],[47.30,10.18],[47.42,12.14],[47.73,13.05],[48.78,13.84],[50.36,12.50],[50.77,14.32],[51.06,14.97],[52.34,14.69],[52.83,14.14],[53.87,14.12],[54.45,16.20],[54.80,9.45],
  ]},
  // Italy (simplified boot)
  { name: 'Italy', color: 0x16b724, opacity: 0.08, points: [
    [47.09,12.16],[46.65,13.71],[45.64,13.77],[44.97,12.51],[43.63,13.52],[42.09,14.80],[41.89,15.98],[40.64,17.94],[40.07,18.52],[39.87,18.30],[39.64,16.52],[38.91,16.60],[38.18,15.63],[37.95,15.64],[38.27,15.21],[37.53,15.08],[36.80,15.24],[37.07,14.50],[37.49,13.01],[38.02,12.75],[38.74,13.20],[39.11,16.10],[39.86,15.62],[40.45,15.35],[41.24,13.63],[42.35,11.15],[43.30,10.20],[43.84,10.17],[44.02,9.72],[43.60,8.02],[44.13,7.63],[44.78,7.17],[45.76,7.04],[45.84,8.55],[45.84,10.70],[46.49,11.50],[47.09,12.16],
  ]},
  // France (simplified)
  { name: 'France', color: 0x16b724, opacity: 0.08, points: [
    [51.09,2.54],[49.39,-0.08],[48.80,-1.85],[48.64,-4.50],[47.99,-4.70],[47.32,-3.25],[46.84,-2.24],[46.32,-1.78],[45.45,-1.14],[44.64,-1.20],[43.29,-1.69],[42.34,0.73],[42.35,3.17],[43.37,3.28],[43.25,5.35],[43.70,7.39],[44.13,7.63],[44.78,7.17],[45.84,8.55],[46.23,7.04],[47.55,7.58],[47.83,7.18],[49.46,6.73],[50.37,6.36],[51.09,2.54],
  ]},
  // Baltic states outline
  { name: 'Baltics', color: 0x16b724, opacity: 0.10, points: [
    [59.94,24.13],[59.44,27.37],[57.80,27.62],[57.57,25.86],[56.17,28.13],[54.68,25.51],[54.41,22.77],[54.95,23.87],[55.72,21.06],[56.37,21.09],[57.07,21.85],[57.57,22.06],[57.97,22.56],[58.55,23.78],[59.47,24.02],[59.94,24.13],
  ]},
];

/* ─── build arc ─── */
function createArc(from, to) {
  const s = new THREE.Vector3(from.x, from.y, 0);
  const e = new THREE.Vector3(to.x, to.y, 0);
  const dist = Math.sqrt((to.x - from.x) ** 2 + (to.y - from.y) ** 2);
  const m = new THREE.Vector3((from.x + to.x) / 2, (from.y + to.y) / 2, dist * 0.25);
  return new THREE.QuadraticBezierCurve3(s, m, e);
}

/* ─── main component ─── */
function RoutesMap3D() {
  const containerRef = useRef(null);
  const labelsRef = useRef(null);
  const [hoveredCity, setHoveredCity] = useState(null);
  const [activeDeliveries, setActiveDeliveries] = useState([]);
  const hoveredRef = useRef(null);
  const cameraRef = useRef(null);
  const rendererRef = useRef(null);
  const isMobileView = typeof window !== 'undefined' && window.innerWidth < 600;

  // Generate random "active deliveries" on mount
  useEffect(() => {
    const statuses = ['В дорозі', 'Завантаження', 'Розвантаження', 'Очікує'];
    const selected = [];
    const shuffled = [...destinations].sort(() => Math.random() - 0.5);
    for (let i = 0; i < 5; i++) {
      selected.push({
        ...shuffled[i],
        status: statuses[Math.floor(Math.random() * statuses.length)],
        progress: Math.floor(Math.random() * 100),
        truck: `THL-${String(Math.floor(Math.random() * 900) + 100)}`,
      });
    }
    setActiveDeliveries(selected);
  }, []);

  // Project 3D → screen for labels
  const project3DtoScreen = useCallback((x, y, z) => {
    if (!cameraRef.current || !rendererRef.current) return null;
    const v = new THREE.Vector3(x, y, z);
    v.project(cameraRef.current);
    const rect = rendererRef.current.domElement.getBoundingClientRect();
    return {
      x: (v.x * 0.5 + 0.5) * rect.width,
      y: (-(v.y) * 0.5 + 0.5) * rect.height,
    };
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let cleanupFn = null;
    let started = false;
    let timeoutId = null;

    // startScene must be declared before tryStart (no hoisting for const)
    const startScene = (w, h) => {
    const scene = new THREE.Scene();
    const isMobile = w < 600;
    const camera = new THREE.PerspectiveCamera(isMobile ? 65 : 50, w / h, 0.1, 100);
    camera.position.set(isMobile ? -0.5 : 0, isMobile ? -0.2 : 0, isMobile ? 10 : 6);
    cameraRef.current = camera;

    const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
    renderer.setSize(w, h);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setClearColor(0x000000, 0);
    container.appendChild(renderer.domElement);
    rendererRef.current = renderer;

    /* ─── scan grid lines ─── */
    for (let i = -5; i <= 5; i++) {
      const geoH = new THREE.BufferGeometry().setFromPoints([
        new THREE.Vector3(-5, i * 0.55, -0.2),
        new THREE.Vector3(5, i * 0.55, -0.2),
      ]);
      const matH = new THREE.LineBasicMaterial({ color: 0x16b724, transparent: true, opacity: 0.03 });
      scene.add(new THREE.Line(geoH, matH));
    }
    for (let i = -9; i <= 9; i++) {
      const geoV = new THREE.BufferGeometry().setFromPoints([
        new THREE.Vector3(i * 0.55, -3, -0.2),
        new THREE.Vector3(i * 0.55, 3, -0.2),
      ]);
      const matV = new THREE.LineBasicMaterial({ color: 0x16b724, transparent: true, opacity: 0.03 });
      scene.add(new THREE.Line(geoV, matV));
    }

    /* ─── country borders ─── */
    countryBorders.forEach(country => {
      const pts = country.points.map(([lat, lon]) => {
        const p = toFlat(lat, lon);
        return new THREE.Vector3(p.x, p.y, -0.05);
      });
      const geo = new THREE.BufferGeometry().setFromPoints(pts);
      const mat = new THREE.LineBasicMaterial({
        color: country.color,
        transparent: true,
        opacity: country.opacity,
      });
      scene.add(new THREE.Line(geo, mat));

      // Fill polygon with subtle color for Ukraine
      if (country.name === 'Ukraine') {
        const shape = new THREE.Shape();
        const flat = country.points.map(([lat, lon]) => toFlat(lat, lon));
        shape.moveTo(flat[0].x, flat[0].y);
        for (let i = 1; i < flat.length; i++) shape.lineTo(flat[i].x, flat[i].y);
        shape.closePath();
        const shapeGeo = new THREE.ShapeGeometry(shape);
        const shapeMat = new THREE.MeshBasicMaterial({
          color: 0x16b724,
          transparent: true,
          opacity: 0.04,
          side: THREE.DoubleSide,
        });
        const shapeMesh = new THREE.Mesh(shapeGeo, shapeMat);
        shapeMesh.position.z = -0.06;
        scene.add(shapeMesh);
      }
    });

    /* ─── home marker ─── */
    const homePos = toFlat(HOME.lat, HOME.lon);

    // Glow circle
    const glowGeo = new THREE.CircleGeometry(0.35, 48);
    const glowMat = new THREE.MeshBasicMaterial({ color: 0x16b724, transparent: true, opacity: 0.06 });
    const glowMesh = new THREE.Mesh(glowGeo, glowMat);
    glowMesh.position.set(homePos.x, homePos.y, -0.01);
    scene.add(glowMesh);

    // Core dot
    const homeGeo = new THREE.CircleGeometry(0.09, 32);
    const homeMat = new THREE.MeshBasicMaterial({ color: 0x4dc247 });
    const homeMesh = new THREE.Mesh(homeGeo, homeMat);
    homeMesh.position.set(homePos.x, homePos.y, 0.02);
    scene.add(homeMesh);

    // Pulse rings (multiple)
    const pulseRingsHome = [];
    for (let r = 0; r < 3; r++) {
      const rGeo = new THREE.RingGeometry(0.12, 0.15, 48);
      const rMat = new THREE.MeshBasicMaterial({ color: 0x4dc247, transparent: true, opacity: 0.4, side: THREE.DoubleSide });
      const rMesh = new THREE.Mesh(rGeo, rMat);
      rMesh.position.set(homePos.x, homePos.y, 0.01);
      scene.add(rMesh);
      pulseRingsHome.push({ mesh: rMesh, mat: rMat, offset: r * 0.8 });
    }

    /* ─── destination dots, arcs, truck dots ─── */
    const meshes = [];
    const cityRings = [];
    const trucks = [];
    const arcLines = [];
    const allCityPositions = []; // for labels

    // store home position for labels
    allCityPositions.push({ name: 'Черкаси', x: homePos.x, y: homePos.y, isHome: true });

    destinations.forEach((dest, i) => {
      const pos = toFlat(dest.lat, dest.lon);
      allCityPositions.push({ name: dest.name, x: pos.x, y: pos.y, isUkraine: dest.country === 'Україна' });

      const isUA = dest.country === 'Україна';

      // City dot
      const dotGeo = new THREE.CircleGeometry(isUA ? 0.05 : 0.04, 16);
      const dotMat = new THREE.MeshBasicMaterial({ color: isUA ? 0x4dc247 : 0x16b724 });
      const dot = new THREE.Mesh(dotGeo, dotMat);
      dot.position.set(pos.x, pos.y, 0.02);
      dot.userData = { city: dest, index: i };
      scene.add(dot);
      meshes.push(dot);

      // Small glow behind dot
      const dotGlowGeo = new THREE.CircleGeometry(0.12, 16);
      const dotGlowMat = new THREE.MeshBasicMaterial({ color: isUA ? 0x4dc247 : 0x16b724, transparent: true, opacity: 0.08 });
      const dotGlowMesh = new THREE.Mesh(dotGlowGeo, dotGlowMat);
      dotGlowMesh.position.set(pos.x, pos.y, 0.0);
      scene.add(dotGlowMesh);

      // Hover ring
      const ringGeo = new THREE.RingGeometry(0.06, 0.10, 24);
      const ringMat = new THREE.MeshBasicMaterial({ color: 0x4dc247, transparent: true, opacity: 0, side: THREE.DoubleSide });
      const ring = new THREE.Mesh(ringGeo, ringMat);
      ring.position.set(pos.x, pos.y, 0.015);
      scene.add(ring);
      cityRings.push({ mesh: ring, mat: ringMat });

      // Arc route
      const curve = createArc(homePos, pos);

      // Draw arc as multiple segments for glow effect
      const arcPts = curve.getPoints(80);
      const arcGeo = new THREE.BufferGeometry().setFromPoints(arcPts);
      const arcMat = new THREE.LineBasicMaterial({
        color: isUA ? 0x4dc247 : 0x16b724,
        transparent: true,
        opacity: isUA ? 0.3 : 0.15,
      });
      const arcLine = new THREE.Line(arcGeo, arcMat);
      scene.add(arcLine);
      arcLines.push({ line: arcLine, mat: arcMat, isUA });

      // Moving "truck" dot with glow trail
      const tGeo = new THREE.CircleGeometry(0.04, 16);
      const tMat = new THREE.MeshBasicMaterial({ color: 0x4dc247 });
      const tMesh = new THREE.Mesh(tGeo, tMat);
      tMesh.position.set(homePos.x, homePos.y, 0.03);
      scene.add(tMesh);

      // Trail (3 fading dots)
      const trails = [];
      for (let tr = 0; tr < 4; tr++) {
        const trGeo = new THREE.CircleGeometry(0.025 - tr * 0.005, 8);
        const trMat = new THREE.MeshBasicMaterial({ color: 0x4dc247, transparent: true, opacity: 0.5 - tr * 0.12 });
        const trMesh = new THREE.Mesh(trGeo, trMat);
        trMesh.position.set(homePos.x, homePos.y, 0.025);
        scene.add(trMesh);
        trails.push({ mesh: trMesh, mat: trMat });
      }

      trucks.push({
        curve,
        mesh: tMesh,
        trails,
        offset: i / destinations.length,
        speed: 0.12 + Math.random() * 0.08,
        isUA,
      });
    });


    /* ─── floating particles ─── */
    const pCount = 300;
    const pPos = new Float32Array(pCount * 3);
    const pVel = [];
    for (let i = 0; i < pCount; i++) {
      pPos[i * 3] = (Math.random() - 0.5) * 14;
      pPos[i * 3 + 1] = (Math.random() - 0.5) * 8;
      pPos[i * 3 + 2] = -0.5 - Math.random() * 2;
      pVel.push({ x: (Math.random() - 0.5) * 0.003, y: (Math.random() - 0.5) * 0.002 });
    }
    const pGeo = new THREE.BufferGeometry();
    pGeo.setAttribute('position', new THREE.BufferAttribute(pPos, 3));
    const pMat = new THREE.PointsMaterial({
      color: 0x16b724,
      size: 0.025,
      transparent: true,
      opacity: 0.25,
      blending: THREE.AdditiveBlending,
    });
    const particles = new THREE.Points(pGeo, pMat);
    scene.add(particles);

    /* ─── mouse & touch controls ─── */
    const raycaster = new THREE.Raycaster();
    const mouse = new THREE.Vector2(99, 99);
    let targetZ = camera.position.z;
    let targetX = camera.position.x;
    let targetY = camera.position.y;
    let isDragging = false;
    let lastTouch = null;
    let lastPinchDist = 0;

    const onMouseMove = (e) => {
      const rect = renderer.domElement.getBoundingClientRect();
      mouse.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      mouse.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
    };
    renderer.domElement.addEventListener('mousemove', onMouseMove);

    // Ctrl+scroll or pinch-on-trackpad to zoom (normal scroll passes through)
    const onWheel = (e) => {
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        targetZ += e.deltaY * 0.01;
        targetZ = Math.max(3, Math.min(18, targetZ));
      }
    };
    renderer.domElement.addEventListener('wheel', onWheel, { passive: false });

    // Mouse drag to pan
    const onMouseDown = (e) => { isDragging = true; lastTouch = { x: e.clientX, y: e.clientY }; };
    const onMouseDrag = (e) => {
      if (!isDragging || !lastTouch) return;
      const dx = (e.clientX - lastTouch.x) * 0.01;
      const dy = (e.clientY - lastTouch.y) * 0.01;
      targetX -= dx;
      targetY += dy;
      lastTouch = { x: e.clientX, y: e.clientY };
    };
    const onMouseUp = () => { isDragging = false; lastTouch = null; };
    renderer.domElement.addEventListener('mousedown', onMouseDown);
    renderer.domElement.addEventListener('mousemove', onMouseDrag);
    renderer.domElement.addEventListener('mouseup', onMouseUp);
    renderer.domElement.addEventListener('mouseleave', onMouseUp);

    // Touch: single finger = pan, two fingers = pinch to zoom
    const onTouchStart = (e) => {
      if (e.touches.length === 1) {
        e.preventDefault();
        lastTouch = { x: e.touches[0].clientX, y: e.touches[0].clientY };
        isDragging = true;
      } else if (e.touches.length === 2) {
        e.preventDefault();
        isDragging = false;
        lastTouch = null;
        const dx = e.touches[0].clientX - e.touches[1].clientX;
        const dy = e.touches[0].clientY - e.touches[1].clientY;
        lastPinchDist = Math.sqrt(dx * dx + dy * dy);
      }
    };
    const onTouchMove = (e) => {
      if (e.touches.length === 1 && isDragging && lastTouch) {
        e.preventDefault();
        const dx = (e.touches[0].clientX - lastTouch.x) * 0.015;
        const dy = (e.touches[0].clientY - lastTouch.y) * 0.015;
        targetX -= dx;
        targetY += dy;
        lastTouch = { x: e.touches[0].clientX, y: e.touches[0].clientY };
      } else if (e.touches.length === 2) {
        e.preventDefault();
        const dx = e.touches[0].clientX - e.touches[1].clientX;
        const dy = e.touches[0].clientY - e.touches[1].clientY;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (lastPinchDist > 0) {
          const delta = (lastPinchDist - dist) * 0.03;
          targetZ += delta;
          targetZ = Math.max(3, Math.min(18, targetZ));
        }
        lastPinchDist = dist;
      }
    };
    const onTouchEnd = () => { lastPinchDist = 0; isDragging = false; lastTouch = null; };
    renderer.domElement.addEventListener('touchstart', onTouchStart, { passive: false });
    renderer.domElement.addEventListener('touchmove', onTouchMove, { passive: false });
    renderer.domElement.addEventListener('touchend', onTouchEnd);

    /* ─── resize ─── */
    const onResize = () => {
      const ww = container.clientWidth;
      const hh = container.clientHeight;
      camera.aspect = ww / hh;
      camera.updateProjectionMatrix();
      renderer.setSize(ww, hh);
    };
    window.addEventListener('resize', onResize);

    /* ─── label update function ─── */
    const updateLabels = () => {
      if (!labelsRef.current) return;
      const children = labelsRef.current.children;
      allCityPositions.forEach((city, idx) => {
        if (idx >= children.length) return;
        const v = new THREE.Vector3(city.x, city.y, 0.05);
        v.project(camera);
        const rect = renderer.domElement.getBoundingClientRect();
        const sx = (v.x * 0.5 + 0.5) * rect.width;
        const sy = (-(v.y) * 0.5 + 0.5) * rect.height;
        children[idx].style.left = sx + 'px';
        children[idx].style.top = (sy - 18) + 'px';
      });
    };

    /* ─── animation ─── */
    let animId;
    const clock = new THREE.Clock();

    const animate = () => {
      animId = requestAnimationFrame(animate);
      const t = clock.getElapsedTime();

      // Home pulse rings expand and fade
      pulseRingsHome.forEach(pr => {
        const phase = ((t + pr.offset) % 2) / 2; // 0→1 over 2s
        const sc = 1 + phase * 3;
        pr.mesh.scale.set(sc, sc, 1);
        pr.mat.opacity = 0.4 * (1 - phase);
      });

      // Truck movement
      trucks.forEach((tr) => {
        const progress = ((t * tr.speed + tr.offset) % 1);
        const pos = tr.curve.getPoint(progress);
        tr.mesh.position.set(pos.x, pos.y, 0.03);

        // Trails follow behind
        tr.trails.forEach((trail, ti) => {
          const tp = Math.max(0, progress - (ti + 1) * 0.02);
          const tpos = tr.curve.getPoint(tp);
          trail.mesh.position.set(tpos.x, tpos.y, 0.025);
          trail.mat.opacity = (0.5 - ti * 0.12) * (progress > 0.05 ? 1 : progress / 0.05);
        });
      });

      // Raycaster hover
      raycaster.setFromCamera(mouse, camera);
      const intersects = raycaster.intersectObjects(meshes);
      let newHovered = null;

      meshes.forEach((m, idx) => {
        const isHit = intersects.length > 0 && intersects[0].object === m;
        if (isHit) {
          newHovered = m.userData.city;
          m.scale.set(2.5, 2.5, 1);
          cityRings[idx].mat.opacity = 0.9;
          const rs = 1.3 + Math.sin(t * 5) * 0.2;
          cityRings[idx].mesh.scale.set(rs, rs, 1);
          // Brighten corresponding arc
          arcLines[idx].mat.opacity = 0.7;
        } else {
          m.scale.lerp(new THREE.Vector3(1, 1, 1), 0.1);
          cityRings[idx].mat.opacity *= 0.9;
          arcLines[idx].mat.opacity += (( arcLines[idx].isUA ? 0.3 : 0.15) - arcLines[idx].mat.opacity) * 0.05;
        }
      });

      if (newHovered !== hoveredRef.current) {
        hoveredRef.current = newHovered;
        setHoveredCity(newHovered);
      }

      // Particles drift
      const positions = particles.geometry.attributes.position.array;
      for (let i = 0; i < pCount; i++) {
        positions[i * 3] += pVel[i].x;
        positions[i * 3 + 1] += pVel[i].y;
        if (positions[i * 3] > 7 || positions[i * 3] < -7) pVel[i].x *= -1;
        if (positions[i * 3 + 1] > 4 || positions[i * 3 + 1] < -4) pVel[i].y *= -1;
      }
      particles.geometry.attributes.position.needsUpdate = true;

      // Smooth camera lerp to target + subtle sway
      camera.position.z += (targetZ - camera.position.z) * 0.08;
      camera.position.x += (targetX + Math.sin(t * 0.15) * 0.05 - camera.position.x) * 0.08;
      camera.position.y += (targetY + Math.cos(t * 0.12) * 0.03 - camera.position.y) * 0.08;

      renderer.render(scene, camera);
      updateLabels();
    };
    animate();

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('resize', onResize);
      renderer.domElement.removeEventListener('mousemove', onMouseMove);
      renderer.domElement.removeEventListener('wheel', onWheel);
      renderer.domElement.removeEventListener('mousedown', onMouseDown);
      renderer.domElement.removeEventListener('mousemove', onMouseDrag);
      renderer.domElement.removeEventListener('mouseup', onMouseUp);
      renderer.domElement.removeEventListener('mouseleave', onMouseUp);
      renderer.domElement.removeEventListener('touchstart', onTouchStart);
      renderer.domElement.removeEventListener('touchmove', onTouchMove);
      renderer.domElement.removeEventListener('touchend', onTouchEnd);
      renderer.dispose();
      if (container.contains(renderer.domElement)) {
        container.removeChild(renderer.domElement);
      }
    }; // end startScene return / cleanup fn
    }; // end startScene

    const tryStart = () => {
      if (started) return;
      const w = container.clientWidth;
      const h = container.clientHeight;
      if (w === 0 || h === 0) return;
      started = true;
      cleanupFn = startScene(w, h);
    };

    // ResizeObserver fires when element gets actual dimensions
    const ro = new ResizeObserver(() => { tryStart(); });
    ro.observe(container);

    // Try immediately and after short delay as fallback
    tryStart();
    timeoutId = setTimeout(tryStart, 150);

    return () => {
      ro.disconnect();
      if (timeoutId) clearTimeout(timeoutId);
      if (cleanupFn) cleanupFn();
    };
  }, []);

  const allPositions = [
    { name: 'Черкаси', isHome: true },
    ...destinations.map(d => ({ name: d.name, isUkraine: d.country === 'Україна' })),
  ];

  return (
    <section style={{
      minHeight: isMobileView ? 'auto' : '100vh',
      padding: isMobileView ? '80px 0 0' : '80px 0 0',
      background: 'linear-gradient(180deg, #0a0a0f 0%, #040a06 50%, #0a0a0f 100%)',
      position: 'relative',
      overflow: 'hidden',
    }}>
      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: 0, padding: '0 24px', position: 'relative', zIndex: 2 }}>
        <div style={{
          display: 'inline-block',
          padding: '6px 20px',
          borderRadius: 30,
          background: 'rgba(22,183,36,0.1)',
          border: '1px solid rgba(22,183,36,0.2)',
          color: '#4dc247',
          fontSize: 13,
          fontWeight: 600,
          letterSpacing: '0.1em',
          textTransform: 'uppercase',
          marginBottom: 12,
        }}>
          Логістичний центр
        </div>
        <h1 style={{
          fontSize: 'clamp(28px, 5vw, 52px)',
          fontWeight: 700,
          color: '#fff',
          marginBottom: 8,
          textTransform: 'uppercase',
          letterSpacing: '0.03em',
        }}>
          <span style={{
            background: 'linear-gradient(135deg, #16b724, #4dc247)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}>Географія перевезень</span>
        </h1>
        <p style={{
          color: 'rgba(255,255,255,0.5)',
          fontSize: 16,
          maxWidth: 600,
          margin: '0 auto',
          lineHeight: 1.6,
        }}>
          {destinations.length} напрямків по Україні та Європі. Від Таллінна до Рима - доставляємо скрізь.
        </p>
      </div>

      {/* Full-width map */}
      <div style={{ position: 'relative', marginTop: isMobileView ? -60 : -10 }}>
        <div
          ref={containerRef}
          style={{
            width: '100%',
            height: isMobileView ? '70vh' : 'calc(100vh - 160px)',
            minHeight: isMobileView ? 400 : 600,
            cursor: 'crosshair',
            position: 'relative',
          }}
        />

        {/* City labels overlay */}
        <div
          ref={labelsRef}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            pointerEvents: 'none',
            overflow: 'hidden',
          }}
        >
          {allPositions.map((city, i) => (
            <div
              key={i}
              style={{
                position: 'absolute',
                transform: 'translateX(-50%)',
                fontSize: isMobileView ? (city.isHome ? 8 : 6) : (city.isHome ? 11 : 9),
                fontWeight: city.isHome ? 700 : 500,
                color: city.isHome ? '#4dc247' : 'rgba(255,255,255,0.5)',
                textShadow: '0 0 8px rgba(0,0,0,0.8)',
                whiteSpace: 'nowrap',
                letterSpacing: '0.05em',
                textTransform: 'uppercase',
              }}
            >
              {city.name}
            </div>
          ))}
        </div>

        {/* Hover tooltip */}
        {hoveredCity && (
          <div style={{
            position: 'absolute',
            top: 20,
            right: 20,
            padding: '16px 24px',
            background: 'rgba(5, 10, 5, 0.92)',
            backdropFilter: 'blur(16px)',
            border: '1px solid rgba(22,183,36,0.4)',
            borderRadius: 14,
            animation: 'fadeInCity 0.2s ease',
            zIndex: 10,
          }}>
            <div style={{
              color: '#4dc247',
              fontSize: 11,
              fontWeight: 600,
              letterSpacing: '0.15em',
              textTransform: 'uppercase',
              marginBottom: 6,
              display: 'flex',
              alignItems: 'center',
              gap: 6,
            }}>
              <span>{hoveredCity.flag}</span>
              {hoveredCity.country}
            </div>
            <div style={{ color: '#fff', fontSize: 20, fontWeight: 700 }}>
              {hoveredCity.name}
            </div>
          </div>
        )}

      </div>



      <style>{`
        @keyframes fadeInCity {
          from { opacity: 0; transform: translateY(-8px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes blink {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.3; }
        }
      `}</style>
    </section>
  );
}

export default RoutesMap3D;
